// The one capture core (C-7): dictation and meeting recording both use it.
//
//   startCapture({ sources, purpose, onFrame, onLevel, onLifecycle }) → CaptureHandle
//
// - The AudioContext is created and resumed synchronously inside the user's
//   gesture (iOS needs that), at the device's default rate. The worklet
//   resamples to 16 kHz with a stateful resampler and frames 100 ms; voice
//   mode can opt into independent 20 ms local activity windows.
// - `mic` uses getUserMedia with echo cancellation, noise suppression and
//   automatic gain on. `tab` uses getDisplayMedia({video, audio}) and stops the
//   video track at once; a share without an audio track is `share_no_audio`.
//   Both sources are mixed into one mono input (S3).
// - Lifecycle events: track ended / muted / unmuted, page hidden / visible,
//   AudioContext suspended and device changes are reported, never acted on
//   here; the caller's policy decides.
// - `stop()` releases the microphone first, then flushes the worklet so the
//   last short frame is emitted, and resolves with the final counts. It is
//   idempotent.
//
// Rejects with `CaptureError{code}`; callers map the code to copy.
import {
  CaptureError,
  type CaptureErrorCode,
  type CaptureHandle,
  type CaptureLifecycle,
  type CaptureOptions,
  type CaptureSource,
} from '../contracts';
import { createCaptureNode, loadCaptureWorklet, type CaptureNode } from './worklet';
import type { FramedPcm } from './framer';
import { prepareTabAudio, TAB_AUDIO_OPTIONS, tabAudioSupported } from './tab-audio';
import { createSourceMeters } from './source-meters';
import { waitForAudioReady } from './audio-ready';

type AudioContextConstructor = new () => AudioContext;

export type CaptureDeps = {
  /** Runs after gesture-time audio setup and before opening any physical source. */
  beforeOpen?: () => Promise<void>;
  createAudioContext?: () => AudioContext;
  getUserMedia?: (constraints: MediaStreamConstraints) => Promise<MediaStream>;
  getDisplayMedia?: ((constraints: DisplayMediaStreamOptions) => Promise<MediaStream>) | null;
  /** Native desktop output capture; unavailable in an ordinary web page. */
  getSystemAudio?: ((context: AudioContext) => Promise<MediaStream>) | null;
  loadWorklet?: (context: AudioContext) => Promise<void>;
  createNode?: (
    context: AudioContext,
    onFrame: (frame: FramedPcm) => void,
    onActivity?: (level: number, samples?: Float32Array, sampleOffset?: number) => void,
  ) => CaptureNode;
  /** Event targets for page lifecycle; the real `document` and `window` by default. */
  pageEvents?: Pick<EventTarget, 'addEventListener' | 'removeEventListener'> | null;
  hiddenState?: () => boolean;
  deviceEvents?: Pick<EventTarget, 'addEventListener' | 'removeEventListener'> | null;
  secureContext?: () => boolean;
  now?: () => number;
  audioReadyTimeoutMs?: number;
};

type BrowserAudioHost = {
  AudioContext?: AudioContextConstructor;
  webkitAudioContext?: AudioContextConstructor;
  AudioWorkletNode?: unknown;
};

const host = () => globalThis as unknown as BrowserAudioHost;
const audioContextConstructor = () => host().AudioContext ?? host().webkitAudioContext;

/** What this browser can capture, without prompting. */
export function captureSupport(): { mic: boolean; tab: boolean; secure: boolean } {
  const media = typeof navigator === 'undefined' ? undefined : navigator.mediaDevices;
  const audio =
    typeof host().AudioWorkletNode === 'function' && audioContextConstructor() !== undefined;
  return {
    mic: audio && typeof media?.getUserMedia === 'function',
    tab:
      audio &&
      typeof media?.getDisplayMedia === 'function' &&
      tabAudioSupported(navigator.userAgent),
    secure: globalThis.isSecureContext !== false,
  };
}

const MEDIA_ERROR_CODES: Record<string, CaptureErrorCode> = {
  NotAllowedError: 'permission_denied',
  SecurityError: 'permission_denied',
  PermissionDeniedError: 'permission_denied',
  NotFoundError: 'no_microphone',
  DevicesNotFoundError: 'no_microphone',
  OverconstrainedError: 'no_microphone',
  NotReadableError: 'microphone_busy',
  TrackStartError: 'microphone_busy',
  AbortError: 'microphone_busy',
};

const errorName = (error: unknown) =>
  typeof error === 'object' && error !== null && 'name' in error
    ? String((error as { name: unknown }).name)
    : '';

/** Map a getUserMedia/getDisplayMedia rejection to a capture code. */
export function captureErrorFor(error: unknown, source: CaptureSource): CaptureError {
  if (error instanceof CaptureError) return error;
  const name = errorName(error);
  if (source === 'tab') {
    if (name === 'NotAllowedError' || name === 'AbortError')
      return new CaptureError('share_cancelled');
    if (name === 'NotSupportedError' || name === 'TypeError')
      return new CaptureError('tab_unsupported');
  }
  return new CaptureError(MEDIA_ERROR_CODES[name] ?? 'unsupported');
}

function resolveDeps(deps: CaptureDeps) {
  const media = typeof navigator === 'undefined' ? undefined : navigator.mediaDevices;
  return {
    createAudioContext:
      deps.createAudioContext ??
      (() => {
        const AudioContextClass = audioContextConstructor();
        if (!AudioContextClass) throw new CaptureError('unsupported');
        return new AudioContextClass();
      }),
    getUserMedia:
      deps.getUserMedia ??
      (media?.getUserMedia ? (c: MediaStreamConstraints) => media.getUserMedia(c) : null),
    getDisplayMedia:
      deps.getDisplayMedia !== undefined
        ? deps.getDisplayMedia
        : media?.getDisplayMedia && tabAudioSupported(navigator.userAgent)
          ? (c: DisplayMediaStreamOptions) => media.getDisplayMedia(c)
          : null,
    getSystemAudio: deps.getSystemAudio ?? null,
    loadWorklet: deps.loadWorklet ?? ((context: AudioContext) => loadCaptureWorklet(context)),
    createNode:
      deps.createNode ??
      ((
        context: AudioContext,
        onFrame: (frame: FramedPcm) => void,
        onActivity?: (level: number, samples?: Float32Array, sampleOffset?: number) => void,
      ) => createCaptureNode(context, onFrame, undefined, onActivity)),
    pageEvents: deps.pageEvents !== undefined ? deps.pageEvents : (globalThis.document ?? null),
    windowEvents: deps.pageEvents !== undefined ? deps.pageEvents : (globalThis.window ?? null),
    hiddenState: deps.hiddenState ?? (() => globalThis.document?.visibilityState === 'hidden'),
    deviceEvents: deps.deviceEvents !== undefined ? deps.deviceEvents : (media ?? null),
    secureContext: deps.secureContext ?? (() => globalThis.isSecureContext !== false),
    now: deps.now ?? (() => Date.now()),
    audioReadyTimeoutMs: deps.audioReadyTimeoutMs ?? 10_000,
  };
}

type Resolved = ReturnType<typeof resolveDeps>;

function micConstraints(deviceId: string | undefined): MediaStreamConstraints {
  return {
    audio: {
      ...(deviceId ? { deviceId: { exact: deviceId } } : {}),
      channelCount: 1,
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl: true,
    },
  };
}

async function openMic(d: Resolved, deviceId: string | undefined): Promise<MediaStream> {
  if (!d.getUserMedia) throw new CaptureError('unsupported');
  try {
    return await d.getUserMedia(micConstraints(deviceId));
  } catch (error) {
    throw captureErrorFor(error, 'mic');
  }
}

async function openSystem(
  d: Resolved,
  context: AudioContext,
  deviceId: string | undefined,
): Promise<MediaStream> {
  if (d.getSystemAudio) return d.getSystemAudio(context);
  if (!deviceId || !d.getUserMedia) throw new CaptureError('no_microphone');
  try {
    return await d.getUserMedia({
      audio: {
        deviceId: { exact: deviceId },
        echoCancellation: false,
        noiseSuppression: false,
        autoGainControl: false,
      },
    });
  } catch (error) {
    throw captureErrorFor(error, 'system');
  }
}

async function openTab(d: Resolved): Promise<MediaStream> {
  if (!d.getDisplayMedia) throw new CaptureError('tab_unsupported');
  let stream: MediaStream;
  try {
    stream = await d.getDisplayMedia(TAB_AUDIO_OPTIONS);
  } catch (error) {
    throw captureErrorFor(error, 'tab');
  }
  return prepareTabAudio(stream);
}

const stopStream = (stream: MediaStream) => {
  for (const track of stream.getTracks()) track.stop();
};

/** Open every requested source; on any failure release what was opened. */
async function openSources(
  d: Resolved,
  context: AudioContext,
  sources: CaptureSource[],
  deviceId: string | undefined,
  systemDeviceId: string | undefined,
): Promise<Array<{ source: CaptureSource; stream: MediaStream }>> {
  const opened: Array<{ source: CaptureSource; stream: MediaStream }> = [];
  try {
    for (const source of sources) {
      const stream =
        source === 'tab'
          ? await openTab(d)
          : source === 'system'
            ? await openSystem(d, context, systemDeviceId)
            : await openMic(d, deviceId);
      opened.push({ source, stream });
    }
    return opened;
  } catch (error) {
    for (const { stream } of opened) stopStream(stream);
    throw error;
  }
}

function validateSources(sources: CaptureSource[]): CaptureSource[] {
  const unique = [...new Set(sources)].filter((s) => s === 'mic' || s === 'tab' || s === 'system');
  if (unique.length === 0) throw new CaptureError('unsupported');
  return unique;
}

type Listener = {
  target: Pick<EventTarget, 'removeEventListener'>;
  type: string;
  fn: EventListener;
};

function listen(
  listeners: Listener[],
  target: Pick<EventTarget, 'addEventListener' | 'removeEventListener'> | null | undefined,
  type: string,
  fn: EventListener,
) {
  if (!target) return;
  target.addEventListener(type, fn);
  listeners.push({ target, type, fn });
}

const TRACK_ENDED: Record<CaptureSource, CaptureLifecycle> = {
  mic: 'mic-ended',
  tab: 'tab-ended',
  system: 'system-ended',
};

function watchLifecycle(
  d: Resolved,
  context: AudioContext,
  opened: Array<{ source: CaptureSource; stream: MediaStream }>,
  report: (event: CaptureLifecycle) => void,
): () => void {
  const listeners: Listener[] = [];
  for (const { source, stream } of opened) {
    for (const track of stream.getAudioTracks()) {
      listen(listeners, track, 'ended', () => report(TRACK_ENDED[source]));
      listen(listeners, track, 'mute', () => report('muted'));
      listen(listeners, track, 'unmute', () => report('unmuted'));
    }
  }
  listen(listeners, d.pageEvents, 'visibilitychange', () =>
    report(d.hiddenState() ? 'hidden' : 'visible'),
  );
  listen(listeners, d.windowEvents, 'pagehide', () => report('hidden'));
  listen(listeners, context, 'statechange', () => {
    if (context.state !== 'running' && context.state !== 'closed') report('context-suspended');
  });
  listen(listeners, d.deviceEvents, 'devicechange', () => report('device-changed'));
  return () => {
    for (const { target, type, fn } of listeners) target.removeEventListener(type, fn);
  };
}

type Graph = { capture: CaptureNode; release(): void };

function buildGraph(
  d: Resolved,
  context: AudioContext,
  opened: Array<{ source: CaptureSource; stream: MediaStream }>,
  onFrame: (frame: FramedPcm) => void,
  onActivity?: (level: number, samples?: Float32Array, sampleOffset?: number) => void,
  onSourceLevels?: CaptureOptions['onSourceLevels'],
): Graph {
  const meters = createSourceMeters(context, opened, onSourceLevels);
  const capture = d.createNode(
    context,
    (frame) => {
      meters.read();
      onFrame(frame);
    },
    onActivity,
  );
  // Two sources are summed; scale them so a double-talk peak rarely clips.
  const mix = context.createGain();
  mix.gain.value = opened.length > 1 ? 0.75 : 1;
  const inputs = opened.map(({ stream }) => context.createMediaStreamSource(stream));
  for (const input of inputs) input.connect(mix);
  mix.connect(capture.node);
  // Some engines only pull a worklet that reaches the destination.
  const silent = context.createGain();
  silent.gain.value = 0;
  capture.node.connect(silent);
  silent.connect(context.destination);
  return {
    capture,
    release: () => {
      meters.release();
      for (const input of inputs) input.disconnect();
      mix.disconnect();
      capture.disconnect();
      silent.disconnect();
    },
  };
}

function preflight(d: Resolved, sources: CaptureSource[]) {
  if (!d.secureContext()) throw new CaptureError('insecure_context');
  if (
    (sources.includes('mic') || (sources.includes('system') && !d.getSystemAudio)) &&
    !d.getUserMedia
  )
    throw new CaptureError('unsupported');
  if (sources.includes('tab') && !d.getDisplayMedia) throw new CaptureError('tab_unsupported');
}

function createContextOnGesture(d: Resolved): { context: AudioContext; resumed: Promise<void> } {
  let context: AudioContext;
  try {
    context = d.createAudioContext();
  } catch (error) {
    throw error instanceof CaptureError ? error : new CaptureError('unsupported');
  }
  const resumed =
    context.state === 'running' ? Promise.resolve() : Promise.resolve(context.resume());
  return { context, resumed };
}

const closeContext = (context: AudioContext) => {
  if (context.state !== 'closed') void context.close().catch(() => {});
};

export async function startCapture(
  options: CaptureOptions,
  deps: CaptureDeps = {},
): Promise<CaptureHandle> {
  const d = resolveDeps(deps);
  const sources = validateSources(options.sources);
  preflight(d, sources);
  if (
    sources.includes('system') &&
    !d.getSystemAudio &&
    (!options.systemDeviceId ||
      (sources.includes('mic') &&
        (!options.deviceId || options.deviceId === options.systemDeviceId)))
  )
    throw new CaptureError('no_microphone');
  // Synchronously inside the gesture: create and resume the context.
  const { context, resumed } = createContextOnGesture(d);
  // Keep the gesture-time promise handled while the person grants access.
  void resumed.catch(() => {});
  if (deps.beforeOpen) {
    try {
      await deps.beforeOpen();
    } catch (error) {
      closeContext(context);
      throw error;
    }
  }
  let opened: Array<{ source: CaptureSource; stream: MediaStream }> = [];
  let graph: Graph;
  let active = true;
  let lastSeq = -1;
  let totalSamples = 0;
  try {
    opened = await openSources(d, context, sources, options.deviceId, options.systemDeviceId);
    await waitForAudioReady(resumed, d.audioReadyTimeoutMs);
    await d.loadWorklet(context).catch(() => {
      throw new CaptureError('unsupported');
    });
    const onActivity = options.onActivity
      ? (level: number, samples?: Float32Array, sampleOffset?: number) => {
          if (active) options.onActivity?.(level, samples, sampleOffset);
        }
      : undefined;
    graph = buildGraph(
      d,
      context,
      opened,
      (frame) => {
        lastSeq = frame.seq;
        totalSamples = frame.sampleOffset + frame.samples;
        options.onLevel?.(frame.level);
        options.onFrame({
          seq: frame.seq,
          sampleOffset: frame.sampleOffset,
          samples: frame.samples,
          pcm: frame.pcm,
        });
      },
      onActivity,
      options.onSourceLevels,
    );
    if (context.state !== 'running')
      await waitForAudioReady(Promise.resolve(context.resume()), d.audioReadyTimeoutMs);
  } catch (error) {
    for (const { stream } of opened) stopStream(stream);
    closeContext(context);
    throw error instanceof CaptureError ? error : new CaptureError('unsupported');
  }
  const unwatch = watchLifecycle(d, context, opened, (event) => options.onLifecycle?.(event));
  let stopping: Promise<{ lastSeq: number; totalSamples: number }> | null = null;
  const stop = () =>
    (stopping ??= (async () => {
      active = false;
      unwatch();
      // Release the microphone at once; the worklet still holds the tail.
      for (const { stream } of opened) stopStream(stream);
      const final = await graph.capture.flush();
      graph.release();
      closeContext(context);
      if (final) {
        lastSeq = final.frames - 1;
        totalSamples = final.samples;
      }
      return { lastSeq, totalSamples };
    })());
  return { startedAt: d.now(), sources, stop };
}
